
import { Component, Vue, Watch } from 'vue-property-decorator'
import { Mutation } from 'vuex-class'

import store, { Actions, Mutations } from '../store'
import { home as vendorHome } from './Vendor/NavBar'
import { home as restaurantHome } from './Restaurant/NavBar'
import { home as adminHome } from './Admin/Navbar'
import PhoneNumber from '@/components/PhoneNumber.vue'
import { CompanyTypeEnum } from '@/firestore'
import RecaptchaVerifier = firebase.auth.RecaptchaVerifier
import firebase from 'firebase'
import LoginService from '@/services/login.service'
import axios from 'axios'
import BrandLogo from '@/components/BrandLogo.vue'

@Component({
  components: {
    PhoneNumber,
    BrandLogo
  }
})
export default class LoginController extends Vue {
  @Mutation(Mutations.UPDATE_USER)
  public updateUser: any

  public email: string = ''
  public password: string = ''
  public error: string = ''

  public loginProcessing = false
  public verificationSent = false
  public valid: boolean = false
  public passwordResetSent: boolean = false
  public rememberPhoneNumber: boolean = false
  public rememberEmail: boolean = false

  public retryTime = 20
  public retryTimeRemaining = 0
  private retryInterval: number | null = null

  public supportEmail = process.env.VUE_APP_SUPPORT_EMAIL

  public phoneNumberAuth: boolean = true
  public phoneNumberAuthConfirmationResult: any = null
  public recaptchaVerifier!: RecaptchaVerifier

  public phoneNumber: string = ''
  public phoneNumberCode: string = ''

  public $refs!: {
    email: HTMLFormElement
    password: HTMLFormElement
    phoneNumber: HTMLFormElement
    smsVerify: Vue | HTMLButtonElement
  }

  public async mounted() {
    if (!this.passwordResetSent) {
      await this.onLoginTypeChange()
    }

    if (store.state.rememberMe) {
      if (store.state.rememberMe.email) {
        this.rememberEmail = true
        this.email = store.state.rememberMe.email
      }

      if (store.state.rememberMe.phoneNumber) {
        this.rememberPhoneNumber = true
        this.phoneNumber = store.state.rememberMe.phoneNumber
      }
    }
  }

  @Watch('phoneNumberAuth')
  public async onLoginTypeChange() {
    this.error = ''

    if (this.phoneNumberAuth) {
      this.$nextTick(async () => {
        if (this.$refs.phoneNumber !== undefined) {
          this.$refs.phoneNumber.focus()
        }
        if (!this.recaptchaVerifier) {
          await this.$firebase
          this.recaptchaVerifier = new firebase.auth.RecaptchaVerifier(
              (this.$refs.smsVerify as Vue).$el as HTMLButtonElement,
              {size: 'invisible'}
          )
        }
      })
    } else {
      this.$nextTick(() => this.$refs.email && this.$refs.email.focus())
    }
  }

  public async resetPassword() {
    try {
      await (await this.$firebase).auth().sendPasswordResetEmail(this.email)
      this.passwordResetSent = true
    } catch (error) {
      this.error = error.message
      if (this.$refs.email) {
        this.$refs.email.focus()
      }
    }
  }

  public async verify() {
    this.error = ''

    if (!this.phoneNumber) {
      this.error = 'Please provide your phone number'
      return
    }

    this.loginProcessing = true

    try {
      const userExists = (await axios.get('/api/onboarding', {
        params: {loginPhoneNumber: this.phoneNumber}
      })).data
    } catch (error) {
      this.error = 'User not found'
      if (this.$refs.phoneNumber) {
        this.$refs.phoneNumber.focus()
      }
      this.loginProcessing = false
      return
    }

    await this.sendVerification()
  }

  public async sendVerification() {
    if (!this.recaptchaVerifier) {
      this.error = 'Unable to verify you are a human... please contact support'
      this.loginProcessing = false
      return
    }

    if (this.phoneNumber) {
      try {
        this.loginProcessing = true
        this.phoneNumberAuthConfirmationResult = await (await this.$firebase).auth()
            .signInWithPhoneNumber(this.phoneNumber, this.recaptchaVerifier)

        this.loginProcessing = false
        this.verificationSent = true
        this.startRetryTimer()
      } catch (error) {
        this.error = error
      }
    }
  }

  public async login() {
    try {
      let userCredential
      if (this.phoneNumberAuth) {
        if (this.rememberPhoneNumber) {
          store.commit(Mutations.UPDATE_REMEMBER_ME, {
            rememberMe: {
              phoneNumber: this.phoneNumber,
              email: store.state.rememberMe.email
            }
          })
        } else if (store.state.rememberMe.phoneNumber) {
          store.commit(Mutations.UPDATE_REMEMBER_ME, {
            rememberMe: {
              phoneNumber: null,
              email: store.state.rememberMe.email
            }
          })
        }
        userCredential = await this.phoneNumberAuthConfirmationResult.confirm(this.phoneNumberCode)
      } else {
        if (this.rememberEmail) {
          store.commit(Mutations.UPDATE_REMEMBER_ME, {
            rememberMe: {
              email: this.email,
              phoneNumber: store.state.rememberMe.phoneNumber
            }
          })
        } else if (store.state.rememberMe.phoneNumber) {
          store.commit(Mutations.UPDATE_REMEMBER_ME, {
            rememberMe: {
              email: null,
              phoneNumber: store.state.rememberMe.phoneNumber
            }
          })
        }
        userCredential = await (await this.$firebase).auth().signInWithEmailAndPassword(this.email, this.password)
      }

      await LoginService.loadUserAndRoute(this.$firebase, userCredential, this.$router)
    } catch (error) {
      if (error.code === 'auth/use-disabled') {
        this.error = 'Your account has been disabled, please contact ' + this.supportEmail + ' for assistance.'
      } else {
        this.error = error.message

        if (error.code === 'auth/wrong-password') {
          if (this.$refs.password) {
            this.$refs.password.focus()
          }
        } else {
          if (this.$refs.email) {
            this.$refs.email.focus()
          }
        }
      }
    }
  }

  private startRetryTimer() {
    this.retryTimeRemaining = this.retryTime
    this.retryInterval = window.setInterval(() => {
      if (this.retryTimeRemaining > 0) {
        --this.retryTimeRemaining
      } else {
        if (typeof this.retryInterval === 'number') {
          clearInterval(this.retryInterval)
          this.retryInterval = null
        }
      }
    }, 1000)
  }

  private newGroup() {
    this.$router.push({name: 'new-group'})
  }
}
